import React, { memo, useMemo, useCallback, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router';
import Tabs, { TabPane } from 'rc-tabs';
import { Button } from '@saleshandy/design-system';
import { useSelector } from 'react-redux';
import { VoiceMailIcon } from '../../../../../../shared/svg';
import './call-logs-header.scss';
import { Reports } from '../../../../../../shared/svg/reports';
import {
  CallLogsListCountsResponse,
  listAllCallLogsPayload,
} from '../../../../types';
import { RootState } from '../../../../../../store/root-reducer';
import { getFormatNumber } from '../../../../../../shared/utils/get-formatted-number';
import hasPermission from '../../../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../../../shared/utils/access-control/enums/permissions';
import { PhoneNumberListResponse } from '../../../../types/phone-numbers';
import { OverlayTooltip } from '../../../../../../shared/design-system/components/overlay/tooltip/overlay-tooltip';

interface IProps {
  onMakeCallClick?: (show: boolean) => void;
  showDialer?: boolean;
  setActiveCallLogsTab?: (tab: string) => void;
  search?: string;
  sendMarkUnseenMissedCallCountRequest?: () => void;
  // callLogsFilters?: CallLogsFiltersState;
  handleCallLogsListRequest?: (
    payload?: listAllCallLogsPayload,
    tabType?: string,
  ) => void;
  callLogsListCountsResponse?: CallLogsListCountsResponse;
  resetCallLogsFilters?: () => void;
  myPhoneNumbersList?: PhoneNumberListResponse;
}

const CallLogsHeader: React.FC<IProps> = ({
  onMakeCallClick = null,
  showDialer,
  setActiveCallLogsTab,
  search,
  sendMarkUnseenMissedCallCountRequest,
  // callLogsFilters,
  handleCallLogsListRequest,
  callLogsListCountsResponse,
  resetCallLogsFilters,
  myPhoneNumbersList,
}) => {
  const location = useLocation();
  const history = useHistory();
  const callLogsFilters = useSelector(
    (state: RootState) => state?.dialer?.callLogsFilters,
  );

  // Determine active tab from URL
  const activeTab = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const type = searchParams.get('type');

    if (location.pathname === '/dialer/call-logs/voicemail-templates') {
      return 'voicemail-templates';
    }
    if (location.pathname === '/dialer/call-logs/reports') {
      return 'reports';
    }
    return type || 'all';
  }, [location]);

  const handleTabChange = useCallback(
    (key: string) => {
      if (key === activeTab) return;
      handleCallLogsListRequest?.(
        {
          pageNumber: 1,
          pageSize: 25,
          searchText: search || '',
        },
        key,
      );
      resetCallLogsFilters?.();

      sendMarkUnseenMissedCallCountRequest?.();
      // Navigate based on tab type
      if (key === 'voicemail-templates' || key === 'reports') {
        history.push(`/dialer/call-logs/${key}`);
      } else {
        history.push(`/dialer/call-logs?type=${key}`);
      }
    },
    [
      activeTab,
      history,
      search,
      callLogsFilters,
      sendMarkUnseenMissedCallCountRequest,
      handleCallLogsListRequest,
      resetCallLogsFilters,
    ],
  );

  const handleMakeCallClick = useCallback(() => {
    if (onMakeCallClick) {
      onMakeCallClick(true);
    }
  }, [onMakeCallClick]);

  useEffect(() => {
    if (setActiveCallLogsTab) {
      setActiveCallLogsTab(activeTab);
    }
  }, [activeTab, setActiveCallLogsTab]);

  return (
    <div className="call-logs-header">
      <div className="call-logs-header__tabs-section">
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          prefixCls="bs-tabs"
          className="bs-tabs-small call-logs-header__tabs"
        >
          <TabPane tab="All Calls" key="all" />
          <TabPane
            tab={
              <span
                className={`call-logs-header__tab-with-icon ${
                  activeTab === 'missed' ? 'active' : ''
                }`}
              >
                <span>Missed Calls</span>
                {callLogsListCountsResponse?.missedCallCount >= 0 && (
                  <div className="call-logs-header__tab-count">
                    <span>
                      {getFormatNumber(
                        callLogsListCountsResponse?.missedCallCount,
                      )}
                    </span>
                  </div>
                )}
              </span>
            }
            key="missed"
          />
          <TabPane
            tab={
              <span
                className={`call-logs-header__tab-with-icon ${
                  activeTab === 'voicemails' ? 'active' : ''
                }`}
              >
                <span>Incoming Voicemail</span>
                {callLogsListCountsResponse?.incomingVoiceMailsCount >= 0 && (
                  <div className="call-logs-header__tab-count">
                    <span>
                      {getFormatNumber(
                        callLogsListCountsResponse?.incomingVoiceMailsCount,
                      )}
                    </span>
                  </div>
                )}
              </span>
            }
            key="voicemails"
          />
          <TabPane
            tab={<div className="call-logs-header-divider" />}
            key="__divider__"
            disabled
          />
          {hasPermission(Permissions.VOICEMAIL_TEMPLATE_READ) && (
            <TabPane
              tab={
                <span className="call-logs-header__tab-with-icon">
                  <VoiceMailIcon width={20} height={20} />
                  <span>Voicemail Templates</span>
                </span>
              }
              key="voicemail-templates"
            />
          )}
          <TabPane
            tab={
              <span className="call-logs-header__tab-with-icon">
                <Reports width={20} height={20} />
                <span>Reports</span>
              </span>
            }
            key="reports"
          />
        </Tabs>
      </div>

      <div className="call-logs-header__actions-section">
        {hasPermission(Permissions.PHONE_CALL_WRITE) && (
          <Button
            className="call-logs-header__make-call-button"
            onClick={handleMakeCallClick}
            disabled={
              showDialer || myPhoneNumbersList?.phoneNumbers?.length === 0
            }
          >
            {myPhoneNumbersList?.phoneNumbers?.length === 0 ? (
              <OverlayTooltip text="No phone number available. Please purchase or get one assigned to start calling.">
                <span>Make a Call</span>
              </OverlayTooltip>
            ) : (
              <span>Make a Call</span>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default memo(CallLogsHeader);
